import React from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronLeft, faCompass, faFilter, faLocationDot} from "@fortawesome/free-solid-svg-icons";
import type {HotspotExplorerGroup, HotspotExplorerHotspot, HotspotExplorerSortMode, HotspotExplorerStats} from "./types";
import {HotspotTable} from "./hotspot-table";
import './hotspot-explorer-shell.scss';

type HotspotExplorerShellProps = {
    eyebrow: string,
    title: string,
    intro: string,
    backLabel?: string,
    onBack?: () => void,
    stats: HotspotExplorerStats,
    panelHeading: string,
    groups: HotspotExplorerGroup[],
    activeGroupCode?: string,
    onSelectGroup: (code: string) => void,
    hotspots: HotspotExplorerHotspot[],
    sortMode: HotspotExplorerSortMode,
    onChangeSort: (sortMode: HotspotExplorerSortMode) => void,
    onOpenHotspot: (locationId: string) => void
}

export function HotspotExplorerShell({
    eyebrow,
    title,
    intro,
    backLabel,
    onBack,
    stats,
    panelHeading,
    groups,
    activeGroupCode,
    onSelectGroup,
    hotspots,
    sortMode,
    onChangeSort,
    onOpenHotspot
}: HotspotExplorerShellProps) {
    return (
        <section className="hotspot-explorer">
            <header className="hotspot-explorer__hero">
                <div className="hotspot-explorer__hero-content">
                    {backLabel && onBack ?
                        <button type="button" className="hotspot-explorer__hero-back" onClick={onBack}>
                            <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                            {backLabel}
                        </button>
                        : null
                    }
                    <p className="hotspot-explorer__eyebrow">
                        <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                        {eyebrow}
                    </p>
                    <h1 className="hotspot-explorer__title">{title}</h1>
                    <p className="hotspot-explorer__intro">{intro}</p>

                    <div className="hotspot-explorer__stats" aria-label="Hotspot summary">
                        <div className="hotspot-explorer__stat">
                            <span className="hotspot-explorer__stat-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faLocationDot} />
                            </span>
                            <span>
                                <strong>{stats.childCount.toLocaleString()}</strong>
                                <span>{stats.childLabel}</span>
                            </span>
                        </div>
                        <div className="hotspot-explorer__stat">
                            <span className="hotspot-explorer__stat-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCompass} />
                            </span>
                            <span>
                                <strong>{hotspots.length.toLocaleString()}</strong>
                                <span>Hotspots Visited</span>
                            </span>
                        </div>
                        <div className="hotspot-explorer__stat">
                            <span className="hotspot-explorer__stat-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faCompass} />
                            </span>
                            <span>
                                <strong>{stats.speciesSeen.toLocaleString()}</strong>
                                <span>Species Seen</span>
                            </span>
                        </div>
                        <div className="hotspot-explorer__stat">
                            <span className="hotspot-explorer__stat-icon" aria-hidden="true">
                                <FontAwesomeIcon icon={faFilter} />
                            </span>
                            <span>
                                <strong>{stats.yearsExploring.toLocaleString()}</strong>
                                <span>{stats.yearsExploring === 1 ? 'Year Exploring' : 'Years Exploring'}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            <div className="hotspot-explorer__content">
                <div className="hotspot-explorer__list-panel">
                    <div className="hotspot-explorer__list-panel-header">
                        <span>Showing {hotspots.length.toLocaleString()} hotspots</span>
                        <div className="hotspot-explorer__sort-tabs" aria-label="Hotspot sort options">
                            <button
                                type="button"
                                className={`hotspot-explorer__sort-tab ${sortMode === 'visits' ? 'is-active' : ''}`}
                                onClick={() => onChangeSort('visits')}
                            >
                                Most Visited
                            </button>
                            <button
                                type="button"
                                className={`hotspot-explorer__sort-tab ${sortMode === 'species' ? 'is-active' : ''}`}
                                onClick={() => onChangeSort('species')}
                            >
                                Most Species
                            </button>
                            <button
                                type="button"
                                className={`hotspot-explorer__sort-tab ${sortMode === 'recent' ? 'is-active' : ''}`}
                                onClick={() => onChangeSort('recent')}
                            >
                                Most Recent
                            </button>
                        </div>
                    </div>
                    <HotspotTable hotspots={hotspots} onOpenHotspot={onOpenHotspot} />
                </div>

                <aside className="hotspot-explorer__panel" aria-label="Explore Your Places">
                    <h2>Explore Your Places</h2>
                    <section className="hotspot-explorer__panel-section">
                        <h3>{panelHeading}</h3>
                        <ul className="hotspot-explorer__group-list">
                            {groups.map(group => (
                                <li key={group.code}>
                                    <button
                                        type="button"
                                        className={`hotspot-explorer__group-button ${activeGroupCode === group.code ? 'is-active' : ''}`}
                                        onClick={() => onSelectGroup(group.code)}
                                    >
                                        <span>{group.label}</span>
                                        <strong>{group.count.toLocaleString()}</strong>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </section>
                </aside>
            </div>
        </section>
    );
}
